import {useEffect, useRef, useState} from 'react';
import * as store from '@/store';
import Input from '@/components/Input';
import Tooltip from '@/components/Tooltip';
import {DEFAULT_TITLE, DOC_TYPE_DEFAULT_TITLE, TITLE_MAX_LENGTH} from '@/constants/document/title';
import {docRename, getNodeCanEdit} from '../../sidebarTreeUtils';
import Icon, {IconSize} from '@/components/Icon';
import {MoreSvg, SidebarAddSvg} from '@/icons';
import classNames from 'classnames';
import styles from './index.module.scss';

interface TreeNodeInputProps {
    text: string;
    onBlur: (value: string) => void;
}

function TreeNodeInput(props: TreeNodeInputProps) {
    const {text, onBlur} = props;
    const [value, setValue] = useState<string>(text);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setValue(e.target.value);
    }
    function handleBlur() {
        onBlur(value);
    }
    return (
        <Input
            autoFocus
            value={value}
            className={styles.nodeInput}
            onBlur={handleBlur}
            onPressEnter={handleBlur}
            onChange={handleChange}
            onMouseDown={(event: React.MouseEvent) => event.stopPropagation()}
            maxLength={TITLE_MAX_LENGTH}
        />
    );
}

// eslint-disable-next-line max-statements
function CustomNode(props: any) {
    const {
        node,
        sidebarMenuTarget,
        isTreeBatchOperate,
    } = props;
    const canEdit = getNodeCanEdit(node);
    const [name, setName] = useState(node.name || '');
    const [editing, setEditing] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const moreBtnRef = useRef(null);
    const addBtnRef = useRef(null);
    const nameRef = useRef<HTMLSpanElement>(null);
    const [showHover, setShowHover] = useState(false);
    const isToc = window.isTocPage;
    function handleInputBlur(value: string) {
        if (value === node.name) {
            setEditing(false);
            return;
        }
        // 策略变更：输入空字符串会按原标题处理 -> 变更为按默认标题处理
        const title = value.trim() || DOC_TYPE_DEFAULT_TITLE[node.type as number] || DEFAULT_TITLE;
        setName(title);
        setEditing(false);
        docRename(node.docGuid, title).then(resName => {
            if (resName !== title) {
                setName(resName);
            }
            node.name = resName;
        }).catch(() => {
            setName(node.name);
        });
    }

    function onDoubleClick() {
        if (canEdit) {
            setEditing(true);
        }
    }

    function handleAddClick(event: React.MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        setShowMenu(true);
        store.setSidebarMenuTarget({
            type: 'add',
            node: node,
            element: addBtnRef.current,
        });
    }

    function handleRename() {
        setEditing(true);
    }

    function handleMoreClick(event: React.MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        setShowMenu(true);
        store.setSidebarMenuTarget({
            type: 'more',
            node: node,
            element: moreBtnRef.current,
            actions: {
                rename: handleRename,
            },
        });
    }

    const onMouseEnter = () => {
        if (nameRef && nameRef.current) {
            // 最大宽度得减去按钮宽度，否则没显示...的标题也会hover显示title
            const scrollWidth = nameRef.current.scrollWidth - 30;
            const clientWidth = nameRef.current.clientWidth;
            setShowHover(scrollWidth > clientWidth);
        }
    };

    useEffect(() => {
        setName(node.name);
    }, [node.name]);

    useEffect(() => {
        if (!sidebarMenuTarget && showMenu) {
            setShowMenu(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarMenuTarget]);

    return (
        <div className={styles.customNode}>
            {
                editing ? (
                    <TreeNodeInput text={name} onBlur={handleInputBlur} />
                ) : (
                    <>
                        {
                            <span
                                ref={nameRef}
                                className={styles.nodeTitle}
                                onDoubleClick={onDoubleClick}
                                title={showHover ? name : ''}
                                onMouseEnter={onMouseEnter}
                            >{name}</span>
                        }
                        {
                            !isToc && canEdit && !isTreeBatchOperate ? (
                                <div
                                    className={classNames(
                                        styles.treeNodeMenuButtons,
                                        showMenu && styles.showMenuButtons,
                                        'sidebar-tree-node-menu-buttons'
                                    )}
                                >
                                    <button
                                        ref={addBtnRef}
                                        className={classNames(styles.addButton, 'node-add-button', 'create-btns')}
                                        onClick={handleAddClick}
                                    >
                                        <Icon svg={SidebarAddSvg} size={IconSize.LG} noSpace />
                                    </button>
                                    <button
                                        ref={moreBtnRef}
                                        className={classNames(styles.moreButton, 'node-more-button')}
                                        onClick={handleMoreClick}
                                    >
                                        <Icon svg={MoreSvg} size={IconSize.XS} noSpace />
                                    </button>
                                </div>
                            ) : null
                        }
                    </>
                )
            }
        </div>
    );
}

export default store.connect([
    store.KEYS.SIDEBAR_MENU_TARGET,
    store.KEYS.IS_TREE_BATCH_OPERATE,
])(CustomNode);
